import { useEffect, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import {
  ExternalLink,
  Bookmark,
  BookmarkCheck,
  MapPin,
  Phone,
  Globe2,
  Search,
  ArrowRight,
  Users2,
  Building2,
  Network,
  Briefcase,
  Server,
} from "lucide-react";
import { toast } from "sonner";
import PaywallGate from "../components/PaywallGate";

const CATEGORIES = {
  music_agency: {
    label: "Music Agencies",
    overline: "PROs · Unions · Guilds",
    icon: Users2,
    intro:
      "Performing Rights Organizations, performer unions, songwriter guilds, and music industry trade associations across the United States and beyond. These bodies advocate for songwriters, composers, and performers and collect royalties on their behalf.",
  },
  music_publisher: {
    label: "Music Publishers",
    overline: "Publishing & administration",
    icon: Building2,
    intro:
      "The world's largest music publishers, independent administrators, and publishing trade groups. From the global majors (Sony, UMPG, Warner Chappell, BMG) to specialist independent publishers and royalty-administration platforms.",
  },
  digital_distribution: {
    label: "Digital Distribution",
    overline: "DSPs · Distributors · Identifiers",
    icon: Network,
    intro:
      "Music distribution platforms, licensing marketplaces, and identifier authorities that deliver independent and major-label catalogs to Spotify, Apple Music, YouTube, TikTok, and the global streaming ecosystem.",
  },
  masters_analytics_legal: {
    label: "Masters · Analytics · Legal",
    overline: "Master rights · Data · Law",
    icon: Briefcase,
    intro:
      "Major recorded-music groups, neighbouring rights agencies, music data and analytics platforms, and legal resources for the music industry — for everything related to master recordings and rights enforcement.",
  },
  domain_infra: {
    label: "Domain & Web Infrastructure",
    overline: "Web presence essentials",
    icon: Server,
    intro:
      "The domain registrars, web infrastructure providers, brand-protection services, and commerce platforms music professionals use to secure their artist names, websites, and direct-to-fan storefronts.",
  },
};

export default function CategoryPage() {
  const { slug } = useParams();
  const { user, refreshUser } = useAuth();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  const meta = CATEGORIES[slug];
  const saved = new Set(user?.saved_agencies || []);

  useEffect(() => {
    if (!meta) return;
    (async () => {
      setLoading(true);
      try {
        const params = { category: slug };
        if (q.trim()) params.q = q.trim();
        const { data } = await api.get("/agencies", { params });
        // Strict alphabetical order — no featured-first reordering on category pages
        const sorted = [...data].sort((a, b) =>
          a.name.localeCompare(b.name, undefined, { sensitivity: "base" })
        );
        setEntries(sorted);
      } finally {
        setLoading(false);
      }
    })();
  }, [slug, meta, q]);

  if (!meta) return <Navigate to="/agencies" replace />;

  const Icon = meta.icon;

  const toggleSave = async (id) => {
    try {
      const { data } = await api.post("/agencies/save", { agency_id: id });
      toast.success(data.action === "saved" ? "Added to your favorites" : "Removed from favorites");
      await refreshUser();
    } catch {
      toast.error("Action failed");
    }
  };

  return (
    <div className="min-h-screen bg-[#0A1128] pt-28 pb-20" data-testid={`category-page-${slug}`}>
      <div className="max-w-7xl mx-auto px-6">
        {/* Breadcrumb */}
        <nav className="text-xs text-slate-500 flex items-center gap-2 mb-6">
          <Link to="/agencies" className="hover:text-white transition-colors">Directory</Link>
          <span>/</span>
          <span className="text-slate-300">Categories</span>
          <span>/</span>
          <span className="text-[#B8D0EE]">{meta.label}</span>
        </nav>

        {/* Hero */}
        <div className="grid lg:grid-cols-12 gap-10 items-end mb-14">
          <div className="lg:col-span-8">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md border border-[#7DA3D9]/25 bg-[#7DA3D9]/5 mb-6">
              <Icon className="w-3.5 h-3.5 text-[#7DA3D9]" />
              <span className="text-[10px] uppercase tracking-[0.25em] text-[#B8D0EE] font-semibold">
                {meta.overline}
              </span>
            </div>
            <h1 className="font-display text-4xl lg:text-7xl tracking-tight leading-[0.98]">
              {meta.label}
            </h1>
            <div className="overline mt-4">
              {entries.length} {entries.length === 1 ? "entity" : "entities"} indexed
            </div>
          </div>
          <div className="lg:col-span-4">
            <p className="text-slate-300 leading-relaxed">{meta.intro}</p>
          </div>
        </div>

        {/* Search within category */}
        <div className="glass-card p-5 mb-10 relative">
          <Search className="w-4 h-4 absolute left-9 top-1/2 -translate-y-1/2 text-slate-500" />
          <input
            className="input-dark pl-10"
            placeholder={`Search within ${meta.label.toLowerCase()}…`}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="category-search-input"
          />
        </div>

        {/* Resources grid OR Paywall */}
        {loading ? (
          <div className="overline text-center py-16">Loading…</div>
        ) : !user?.has_complete_access ? (
          <PaywallGate
            title={`The ${meta.label} category is members-only`}
            subtitle={`Unlock the full ${entries.length}-entity ${meta.label} catalog (and all other OSRR categories) for a one-time $6.95. Lifetime access.`}
            ctaLabel="Unlock the catalog — $6.95"
          />
        ) : entries.length === 0 ? (
          <div className="text-center py-16 text-slate-400">No entries match your search.</div>
        ) : (
          <>
            {/* A–Z jump nav */}
            {entries.length > 6 && (
              <div className="flex flex-wrap items-center gap-1 mb-6" data-testid="alpha-nav">
                <span className="overline mr-3">Jump to</span>
                {Array.from(new Set(entries.map((e) => e.name[0].toUpperCase()))).map((letter) => (
                  <a
                    key={letter}
                    href={`#letter-${letter}`}
                    className="px-2 py-1 text-xs font-mono text-slate-400 hover:text-[#B8D0EE] border border-transparent hover:border-[#7DA3D9]/30 hover:bg-[#7DA3D9]/5 rounded transition-all"
                    data-testid={`alpha-${letter}`}
                  >
                    {letter}
                  </a>
                ))}
              </div>
            )}

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="category-grid">
              {entries.map((a, i) => {
                const letter = a.name[0].toUpperCase();
                const prevLetter = i > 0 ? entries[i - 1].name[0].toUpperCase() : null;
                const isNewLetter = letter !== prevLetter;
                return (
                  <div key={a.id} className="contents">
                    {isNewLetter && (
                      <div
                        id={`letter-${letter}`}
                        className="col-span-full font-display text-3xl text-[#7DA3D9]/40 mt-2 mb-1 scroll-mt-32 select-none"
                        data-testid={`section-letter-${letter}`}
                      >
                        {letter}
                      </div>
                    )}
                    <EntityCard agency={a} saved={saved.has(a.id)} onToggleSave={() => toggleSave(a.id)} />
                  </div>
                );
              })}
            </div>
          </>
        )}

        {/* Other categories */}
        <section className="mt-20" data-testid="other-categories">
          <div className="overline mb-3">Other categories</div>
          <h2 className="font-display text-2xl lg:text-3xl mb-6">Browse the full registry</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {Object.entries(CATEGORIES)
              .filter(([k]) => k !== slug)
              .map(([k, c]) => {
                const CIcon = c.icon;
                return (
                  <Link
                    key={k}
                    to={`/category/${k}`}
                    className="glass-card p-5 group"
                    data-testid={`other-cat-${k}`}
                  >
                    <CIcon className="w-5 h-5 text-[#7DA3D9]" />
                    <div className="font-display text-lg mt-3 group-hover:text-[#B8D0EE] transition-colors">
                      {c.label}
                    </div>
                    <div className="text-xs text-slate-400 mt-1">{c.overline}</div>
                  </Link>
                );
              })}
          </div>
        </section>
      </div>
    </div>
  );
}

function EntityCard({ agency, saved, onToggleSave }) {
  return (
    <div
      className="glass-card p-7 flex flex-col fade-up"
      data-testid={`cat-entity-${agency.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
    >
      <div className="flex items-start justify-between gap-3">
        {agency.logo_url ? (
          <a
            href={agency.website}
            target="_blank"
            rel="noopener noreferrer"
            className="group/logo flex flex-col items-start gap-1.5"
            title={`Visit ${agency.name} → ${agency.website}`}
            data-testid={`cat-logo-${agency.id}`}
          >
            <div className="w-16 h-16 rounded-md bg-white p-2 border border-white/10 group-hover/logo:border-[#7DA3D9] group-hover/logo:scale-105 transition-all shadow-lg">
              <img
                src={agency.logo_url}
                alt={`${agency.name} logo · click to visit official site`}
                className="w-full h-full object-contain"
                onError={(e) => {
                  e.currentTarget.onerror = null;
                  e.currentTarget.src = `https://www.google.com/s2/favicons?domain=${new URL(agency.website).hostname}&sz=128`;
                }}
              />
            </div>
            <span className="text-[10px] uppercase tracking-[0.15em] text-slate-500 group-hover/logo:text-[#7DA3D9] transition-colors">
              Click logo · Visit site ↗
            </span>
          </a>
        ) : (
          <div className="text-3xl">{agency.flag || "🌐"}</div>
        )}
        <button
          onClick={onToggleSave}
          className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border transition-all text-xs ${
            saved
              ? "border-[#7DA3D9]/40 bg-[#7DA3D9]/10 text-[#B8D0EE]"
              : "border-white/10 bg-white/[0.02] text-slate-400 hover:border-[#7DA3D9]/40 hover:text-[#7DA3D9] hover:bg-[#7DA3D9]/5"
          }`}
          aria-label="Save to Favorites"
          title={saved ? "In your favorites" : "Save to Favorites"}
          data-testid={`cat-save-${agency.id}`}
        >
          {saved ? <><BookmarkCheck className="w-3.5 h-3.5" /> Saved</> : <><Bookmark className="w-3.5 h-3.5" /> Save</>}
        </button>
      </div>
      <Link to={`/agencies/${agency.id}`} className="mt-4 group">
        <h3 className="font-display text-2xl group-hover:text-[#B8D0EE] transition-colors">{agency.name}</h3>
        <div className="text-sm text-slate-400 mt-1">
          {agency.country}{agency.founded ? ` · Est. ${agency.founded}` : ""}
        </div>
      </Link>
      <p className="text-sm text-slate-300 mt-4 leading-relaxed flex-1 line-clamp-3">
        {agency.description}
      </p>

      {(agency.address || agency.phone) && (
        <div className="mt-4 space-y-1.5 text-xs text-slate-400">
          {agency.address && (
            <div className="flex items-start gap-1.5">
              <MapPin className="w-3 h-3 text-[#7DA3D9] mt-0.5 flex-shrink-0" />
              <span className="line-clamp-2">{agency.address}</span>
            </div>
          )}
          {agency.phone && (
            <div className="flex items-center gap-1.5">
              <Phone className="w-3 h-3 text-[#7DA3D9] flex-shrink-0" />
              <span>{agency.phone}</span>
            </div>
          )}
        </div>
      )}

      <div className="divider-gold my-5" />
      <div className="flex items-center justify-between text-sm">
        <Link to={`/agencies/${agency.id}`} className="text-[#7DA3D9] hover:text-[#B8D0EE]">
          View details →
        </Link>
        <a
          href={agency.website}
          target="_blank"
          rel="noopener noreferrer"
          className="text-slate-400 hover:text-white inline-flex items-center gap-1.5"
        >
          <Globe2 className="w-3.5 h-3.5" /> Website <ExternalLink className="w-3 h-3" />
        </a>
      </div>
    </div>
  );
}
